import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import ZefixLookup from "@/components/ZefixLookup";
import UidDuplicateBanner from "@/components/UidDuplicateBanner";

/** 3-Schritt-Wizard zur Neuanlage einer Firma.
 *
 * Schritt 1: Basisdaten (Name, Währung)
 * Schritt 2: Adresse & IBAN (optional)
 * Schritt 3: MWST & Standardsätze
 */
export function NewCompany() {
  const navigate = useNavigate();
  const { setCompany } = useCompany();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    currency: "CHF",
    address: "",
    postal_code: "",
    city: "",
    country: "CH",
    uid_number: "",
    iban: "",
    bank_name: "",
    vat_enabled: true,
    default_vat_rate: 8.1,
    company_type: "business",
  });
  const [params] = useSearchParams();
  React.useEffect(() => {
    // Vorbelegung aus Link, z.B. /new-company?type=privat&name=Privat%20Alex (Privatkonto aus dem Posteingang)
    if (params.get("type") === "privat") {
      setForm((f) => ({ ...f, company_type: "privat", vat_enabled: false, name: params.get("name") || f.name || "Privat" }));
    }
  }, [params]);
  const isPrivat = form.company_type === "privat";

  const canNext1 = form.name.trim().length >= 2;
  // Iter115: Step 2 = Pflicht-Stammdaten (Strasse, PLZ, Ort, IBAN)
  const ibanNorm = (form.iban || "").replace(/\s+/g, "").toUpperCase();
  const ibanValid =
    ibanNorm.length >= 15 &&
    ibanNorm.length <= 34 &&
    /^[A-Z]{2}[0-9]{2}/.test(ibanNorm);
  const canNext2 =
    form.address.trim().length >= 2 &&
    form.postal_code.trim().length >= 4 &&
    form.city.trim().length >= 2 &&
    (ibanValid || (isPrivat && !ibanNorm));

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        currency: form.currency,
        street: form.address.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        country: form.country || "CH",
        uid: form.uid_number || null,
        iban: ibanNorm || null,
        bank_name: form.bank_name || null,
        company_type: form.company_type,
        vat_enabled: isPrivat ? false : !!form.vat_enabled,
        default_vat_rate:
          form.default_vat_rate === "" || form.default_vat_rate === null
            ? null
            : Number(form.default_vat_rate),
      };
      const res = await apiClient.post(`/my/companies`, payload);
      setCompany(res.data);
      toast.success(`Firma "${res.data.name}" erfolgreich angelegt.`);
      if (isPrivat) {
        navigate("/");
        return;
      }
      // Schritt 1 nach dem Anlegen: Kontenplan einrichten (PDF / andere Firma / KI)
      navigate("/settings?onboarding=chart#section-kontenrahmen");
    } catch (e) {
      const raw = e?.response?.data?._rawDetail;
      if (e?.response?.status === 402 || raw?.error === "plan_limit_exceeded") {
        toast.error(e?.response?.data?.detail || "Tarif-Limit erreicht: keine weitere Firma möglich.", {
          duration: 12000,
          action: { label: "Tarif wechseln", onClick: () => navigate(raw?.upgrade_url || "/billing") },
        });
        return;
      }
      const detail = e?.response?.data?.detail;
      let msg = "Fehler beim Anlegen der Firma.";
      if (Array.isArray(detail)) {
        msg = detail.map((d) => `${d.loc?.[1] || ""}: ${d.msg}`).join(" · ");
      } else if (detail) {
        msg = String(detail);
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const StepBadge = ({ index, label }) => {
    const active = step === index;
    const done = step > index;
    return (
      <div className="flex items-center gap-2" data-testid={`wizard-step-${index}`}>
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
            done
              ? "bg-emerald-500 text-white"
              : active
              ? "bg-slate-900 text-white"
              : "bg-slate-200 text-slate-500"
          }`}
        >
          {done ? <Check className="h-3.5 w-3.5" /> : index}
        </div>
        <span className={`text-xs font-medium ${active ? "text-slate-900" : "text-slate-500"}`}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="new-company-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shadow-sm">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Neue Firma anlegen
          </h1>
          <p className="text-sm text-slate-500">
            Legen Sie eine weitere GmbH an — Ihre bestehenden Firmen bleiben erhalten.
          </p>
        </div>
      </div>

      <Card className="p-5 border border-slate-200 bg-white shadow-sm">
        {/* Step indicator */}
        <div className="flex items-center justify-between gap-2 mb-6">
          <StepBadge index={1} label="Basisdaten" />
          <div className="flex-1 h-px bg-slate-200 mx-2" />
          <StepBadge index={2} label="Adresse & Bank" />
          <div className="flex-1 h-px bg-slate-200 mx-2" />
          <StepBadge index={3} label="MWST" />
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1-content">
            <div className="grid grid-cols-2 gap-2" data-testid="company-type-switch">
              {[["business", "Geschäft / Firma", "Mit MWST, KMU-Kontenplan, Offerten & Rechnungen"], ["privat", "Privat-Buchhaltung", "Ohne MWST, private Kategorien (Wohnen, Haushalt, Mobilität …)"]].map(([id, label, desc]) => (
                <button key={id} type="button" onClick={() => setForm({ ...form, company_type: id, vat_enabled: id === "business" })} data-testid={`company-type-${id}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.company_type === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <div className="font-medium text-sm">{label}</div>
                  <div className={`text-xs mt-0.5 ${form.company_type === id ? "text-slate-200" : "text-slate-500"}`}>{desc}</div>
                </button>
              ))}
            </div>
            {!isPrivat && (
            <div className="space-y-1.5">
              <Label>Aus Schweizer Handelsregister übernehmen (optional)</Label>
              <ZefixLookup
                onSelect={(r) => setForm((prev) => ({
                  ...prev,
                  name: r.name || prev.name,
                  city: r.city || prev.city,
                  uid_number: r.uid_formatted || r.uid || prev.uid_number,
                  address: r.street || prev.address,
                  postal_code: r.postal_code || prev.postal_code,
                }))}
                placeholder="Firmennamen tippen — Zefix schlägt vor…"
              />
              <p className="text-xs text-slate-500">
                Firmenname, UID, Ort, Straße & PLZ werden automatisch übernommen (Zefix + BFS UID-Register).
              </p>
              <UidDuplicateBanner uid={form.uid_number} />
            </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="company-name">{isPrivat ? "Name (z.B. «Privat Michael»)" : "Firmenname"} <span className="text-rose-500">*</span></Label>
              <Input
                id="company-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Meine GmbH"
                autoFocus
                data-testid="wizard-company-name-input"
              />
              <p className="text-xs text-slate-500">
                Wird als Absender auf Rechnungen und Mahnungen verwendet.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-currency">Währung</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger id="company-currency" data-testid="wizard-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHF">CHF — Schweizer Franken</SelectItem>
                  <SelectItem value="EUR">EUR — Euro</SelectItem>
                  <SelectItem value="USD">USD — US Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2-content">
            <p className="text-xs text-slate-500">Pflichtfelder sind mit <span className="text-rose-500">*</span> markiert.</p>
            <div className="space-y-1.5">
              <Label htmlFor="company-address">Strasse & Nr. <span className="text-rose-500">*</span></Label>
              <Input
                id="company-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Bahnhofstrasse 1"
                data-testid="wizard-address-input"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="company-postal">PLZ <span className="text-rose-500">*</span></Label>
                <Input
                  id="company-postal"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  data-testid="wizard-postal-input"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company-city">Ort <span className="text-rose-500">*</span></Label>
                <Input
                  id="company-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="wizard-city-input"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-uid">UID-Nummer (optional)</Label>
              <Input
                id="company-uid"
                value={form.uid_number}
                onChange={(e) => setForm({ ...form, uid_number: e.target.value })}
                placeholder="CHE-123.456.789"
                data-testid="wizard-uid-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-iban">IBAN {isPrivat ? <span className="text-slate-400">(optional – eigenes Bankkonto)</span> : <span className="text-rose-500">*</span>}</Label>
              <Input
                id="company-iban"
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="CH00 0000 0000 0000 0000 0"
                data-testid="wizard-iban-input"
                required
                className={form.iban && !ibanValid ? "border-rose-300" : ""}
              />
              {form.iban && !ibanValid && (
                <p className="text-[11px] text-rose-600">IBAN-Format ungültig (z.B. CH93 0076 2011 6238 5295 7)</p>
              )}
              <p className="text-[11px] text-slate-500">
                Wird benötigt für Auto-Routing von Bank-Statements und Swiss QR-Rechnungen.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-bank">Bankname (optional)</Label>
              <Input
                id="company-bank"
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                data-testid="wizard-bank-input"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3-content">
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <input
                type="checkbox"
                id="vat-enabled"
                checked={form.vat_enabled}
                disabled={isPrivat}
                onChange={(e) => setForm({ ...form, vat_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
                data-testid="wizard-vat-enabled-checkbox"
              />
              <Label htmlFor="vat-enabled" className="cursor-pointer">
                MWST-pflichtig
              </Label>
              <span className="text-xs text-slate-500">
                (Deaktivieren für Firmen unter CHF 100'000 Umsatz/Jahr)
              </span>
            </div>
            {form.vat_enabled && (
              <div className="space-y-1.5">
                <Label htmlFor="vat-rate">Standard-MWST-Satz (%)</Label>
                <Input
                  id="vat-rate"
                  type="number"
                  step="0.1"
                  value={form.default_vat_rate}
                  onChange={(e) => setForm({ ...form, default_vat_rate: e.target.value })}
                  data-testid="wizard-vat-rate-input"
                />
                <p className="text-xs text-slate-500">
                  Schweiz: 8.1% (Normalsatz), 2.6% (ermässigt), 3.8% (Beherbergung).
                </p>
              </div>
            )}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <Check className="h-4 w-4" /> Fast geschafft
              </div>
              <p className="text-xs text-emerald-800 leading-relaxed">
                Nach dem Anlegen wird ein Standard-Kontenplan (Schweizer KMU) automatisch erstellt.
                Sie können Konten, MWST-Sätze und SMTP-Einstellungen später in den Einstellungen anpassen.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
            disabled={saving}
            data-testid="wizard-back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {step === 1 ? "Abbrechen" : "Zurück"}
          </Button>
          {step < 3 ? (
            <Button
              size="sm"
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              className="rounded-full bg-slate-900 text-white"
              data-testid="wizard-next-button"
            >
              Weiter
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={saving || !canNext1}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="wizard-create-button"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              {saving ? "Erstellt ..." : "Firma anlegen"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

export default NewCompany;
